import { ApolloError } from "@apollo/client";
import axios from "axios";

import { isRecord } from "./record";

function formatExtensionPayload(payload: unknown): string | undefined {
  if (payload == null || payload === "") return undefined;
  if (typeof payload === "string") return payload;
  if (typeof payload === "number" || typeof payload === "boolean") {
    return String(payload);
  }
  try {
    return JSON.stringify(payload);
  } catch {
    return String(payload);
  }
}

/** Toast title prefers `title`; body lists GraphQL `message` and payload when useful. */
function mergeGraphQlParts(parts: {
  message?: string;
  title?: string;
  payloadFormatted?: string;
}): { message: string; description?: string } {
  const title = parts.title?.trim();
  const msg = parts.message?.trim() ?? "";
  const payloadDesc = parts.payloadFormatted;

  if (title) {
    const descParts: string[] = [];
    if (msg && msg !== title) {
      descParts.push(msg);
    }
    if (payloadDesc) {
      descParts.push(payloadDesc);
    }
    const description =
      descParts.length > 0 ? descParts.join("\n\n") : undefined;
    return { message: title, ...(description && { description }) };
  }

  if (msg) {
    return {
      message: msg,
      ...(payloadDesc && { description: payloadDesc }),
    };
  }

  return { message: "Error" };
}

export function toError(input: unknown): Error {
  if (input instanceof Error) {
    return input;
  }

  return new Error(getErrorMessage(input));
}

export function getErrorMessage(input: unknown): string {
  if (isWithMessage(input)) {
    return input.message;
  }

  try {
    return JSON.stringify(input);
  } catch {
    return String(input);
  }
}

/** Raw GraphQL fields from a network-wrapped error (keeps `message` for legacy checks). */
export function getGraphQLErrorMessage(error: unknown):
  | {
      message?: string;
      title?: string;
      description?: string;
    }
  | undefined {
  if (
    error instanceof ApolloError &&
    error.networkError &&
    "result" in error.networkError &&
    typeof error.networkError.result === "object" &&
    error.networkError.result !== null &&
    "errors" in error.networkError.result &&
    Array.isArray(error.networkError.result.errors)
  ) {
    const graphQlError = error.networkError.result.errors[0];
    const ext = graphQlError?.extensions as Record<string, unknown> | undefined;
    return {
      message: graphQlError?.message,
      title:
        typeof ext?.title === "string" && ext.title.trim()
          ? ext.title.trim()
          : undefined,
      description: formatExtensionPayload(ext?.payload),
    };
  }
}

/**
 * Builds antd notification props: prefers server `extensions.title`, then GraphQL message.
 */
export function getServerErrorNotification(
  error: unknown,
  fallbackMessage: string
): { message: string; description?: string } {
  if (error instanceof ApolloError) {
    const gqlErr = error.graphQLErrors?.[0];
    if (gqlErr) {
      const ext = gqlErr.extensions as Record<string, unknown> | undefined;
      return mergeGraphQlParts({
        message: gqlErr.message,
        title:
          typeof ext?.title === "string" && ext.title.trim()
            ? ext.title.trim()
            : undefined,
        payloadFormatted: formatExtensionPayload(ext?.payload),
      });
    }

    const nw = getGraphQLErrorMessage(error);
    if (nw && (nw.message?.trim() || nw.title?.trim())) {
      return mergeGraphQlParts({
        message: nw.message,
        title: nw.title,
        payloadFormatted: nw.description,
      });
    }

    if (error.message?.trim()) {
      return { message: error.message };
    }
  }

  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (isRecord(data)) {
      const serverTitle =
        typeof data.title === "string" && data.title.trim()
          ? data.title.trim()
          : undefined;
      const serverMsg =
        typeof data.message === "string" && data.message.trim()
          ? data.message.trim()
          : "";
      if (serverTitle) {
        const description =
          serverMsg && serverMsg !== serverTitle ? serverMsg : undefined;
        return {
          message: serverTitle,
          ...(description && { description }),
        };
      }
      if (serverMsg) {
        return { message: serverMsg };
      }
    }
  }

  const fromGeneric = getErrorMessage(error);
  if (
    fromGeneric &&
    fromGeneric.trim() &&
    fromGeneric !== "{}" &&
    fromGeneric !== "[object Object]"
  ) {
    return { message: fromGeneric };
  }

  return { message: fallbackMessage };
}
function isWithMessage(input: unknown): input is { message: string } {
  return isRecord(input) && typeof input.message === "string";
}

/**
 * `extensions.type` of the first GraphQL error (a problem URI such as
 * `https://api.kontist.com/problems/duplicate_tax_number`). Read from the
 * parsed `graphQLErrors`, or from the raw network result when the server
 * answered with a non-200 status (400/409), where Apollo wraps the body.
 */
export function getGraphQlErrorType(error: unknown): string | undefined {
  if (!(error instanceof ApolloError)) {
    return undefined;
  }

  const fromGraphQlErrors = error.graphQLErrors?.[0]?.extensions?.type;
  if (typeof fromGraphQlErrors === "string") {
    return fromGraphQlErrors;
  }

  const networkResult = (
    error.networkError as { result?: { errors?: unknown } } | null
  )?.result;
  const firstError = Array.isArray(networkResult?.errors)
    ? (networkResult?.errors[0] as { extensions?: { type?: unknown } })
    : undefined;
  return typeof firstError?.extensions?.type === "string"
    ? firstError.extensions.type
    : undefined;
}
